import { useContext, useEffect, useState } from "react";
import { useAccess } from "../../../../access/hooks/useAccess";
import { billingAllows } from "../../billingAccess";
import { Button, SectionHeader, Tags } from "../../../ui";
import { Context } from "../../../../context/Context";
import { formatSar } from "../../../../utils/billingMoney";
import {
  getServiceFulfillmentQueueAPI,
  listScheduledStopsAPI,
  transitionServiceOrderFulfillmentAPI,
} from "../../../../utils/api/billing";
import { useBillingAdminText } from "../billingAdminI18n";

const COLUMNS = ["new", "in_progress", "done", "cancelled"];
const NEXT_STATUS = {
  new: ["in_progress", "cancelled"],
  in_progress: ["done", "cancelled"],
  done: [],
  cancelled: [],
};

const formatDate = (value, lang) => (value ? new Date(value).toLocaleDateString(lang === "ar" ? "ar-SA" : "en-US") : "-");

const FollowUpTab = ({ access = null } = {}) => {
  const { lang = "en" } = useContext(Context) || {};
  const { can } = useAccess();
  const text = useBillingAdminText(lang);
  const token = localStorage.getItem("auth-token");
  const canView = billingAllows(can("billing.admin.view"), access?.canViewBillingAdmin);
  const canManageFulfillment = billingAllows(can("billing.admin.fulfillment.manage"), access?.canManageServiceFulfillment);

  const [stops, setStops] = useState({ data: [] });
  const [queue, setQueue] = useState({ new: [], in_progress: [], done: [], cancelled: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [moving, setMoving] = useState("");

  const load = async () => {
    if (!canView || !token) return;
    setLoading(true);
    setError("");
    try {
      const [stopsResult, queueResult] = await Promise.all([
        listScheduledStopsAPI(token, { limit: 50 }),
        getServiceFulfillmentQueueAPI(token),
      ]);
      setStops({ data: stopsResult?.data || [] });
      setQueue(queueResult?.data || { new: [], in_progress: [], done: [], cancelled: [] });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canView]);

  const move = async (order, toStatus) => {
    if (!canManageFulfillment) return;
    setMoving(order._id);
    setError("");
    try {
      await transitionServiceOrderFulfillmentAPI(token, order._id, { toStatus });
      await load();
    } catch (err) {
      setError(err.message);
    } finally {
      setMoving("");
    }
  };

  if (!canView) {
    return <div className="billing-admin-denied">{text.subscriptions.denied}</div>;
  }

  return (
    <section className="billing-followup-v141">
      <SectionHeader title={text.followUpV141.title} description={text.followUpV141.description} />
      {error && <div className="billing-error">{error}</div>}
      {loading && <div className="billing-loading">{text.common.loading}</div>}

      <div className="billing-followup-grid">
        <div className="billing-followup-card">
          <div className="billing-followup-head">
            <strong>{text.followUpV141.scheduledStops.title}</strong>
            <span>{stops.data.length}</span>
          </div>
          {stops.data.length === 0 ? (
            <div className="billing-empty-note">{text.followUpV141.scheduledStops.empty}</div>
          ) : (
            stops.data.map((stop) => (
              <div key={stop.subscriptionId} className="billing-stop-row">
                <div>
                  <strong>{stop.restaurant?.name || stop.storeId}</strong>
                  <small>
                    {stop.storeId} · {stop.planCode || stop.type}
                  </small>
                </div>
                <div>
                  <Tags variant="warning">{stop.type === "platform" ? "LUD" : text.subscriptions.ledgerLabels.intgStatus}</Tags>
                </div>
                <div>
                  <strong>
                    {text.followUpV141.scheduledStops.paidThrough}: {formatDate(stop.paidThroughDate, lang)}
                  </strong>
                  <small>{text.followUpV141.scheduledStops.remainingDays.replace("{days}", stop.remainingDays ?? "-")}</small>
                  <small>{text.followUpV141.scheduledStops.scheduledNote}</small>
                </div>
              </div>
            ))
          )}
        </div>

        <div className="billing-followup-card">
          <div className="billing-followup-head">
            <div>
              <strong>{text.followUpV141.serviceQueue.title}</strong>
              <div className="billing-followup-note">{text.followUpV141.serviceQueue.note}</div>
            </div>
          </div>
          <div className="billing-kanban-board">
            {COLUMNS.map((column) => (
              <div className="billing-kanban-column" key={column}>
                <div className="billing-kanban-column-head">
                  <span>{text.followUpV141.serviceQueue.columns[column]}</span>
                  <span className="billing-kanban-count">{queue[column]?.length || 0}</span>
                </div>
                <div className="billing-kanban-list">
                  {(queue[column] || []).length === 0 ? (
                    <div className="billing-kanban-empty">{text.followUpV141.serviceQueue.empty}</div>
                  ) : (
                    queue[column].map((order) => (
                      <div key={order._id} className="billing-kanban-card">
                        <strong>{order.serviceSnapshot?.nameAr || order.serviceSnapshot?.nameEn}</strong>
                        <span className="billing-kanban-meta">{order.restaurant?.name || order.storeId}</span>
                        <span className="billing-kanban-meta">
                          {order.quantity} × {formatSar(order.serviceSnapshot?.unitPriceMinor || 0, lang)}
                        </span>
                        {canManageFulfillment && NEXT_STATUS[column]?.length > 0 && (
                          <div className="billing-kanban-actions">
                            {NEXT_STATUS[column].map((nextStatus) => (
                              <Button
                                key={nextStatus}
                                size="sm"
                                appearance="outline"
                                loading={moving === order._id}
                                onClick={() => move(order, nextStatus)}
                              >
                                {text.followUpV141.serviceQueue.columns[nextStatus]}
                              </Button>
                            ))}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default FollowUpTab;
